import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function AIGeneratorModal({ user, activityType, onClose, onSuccess }) {
  const [topic, setTopic] = useState('');
  const [textContent, setTextContent] = useState(''); // For TextHunt
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getCountLabel = () => {
    if (activityType === 'quiz') return 'Número de preguntas';
    if (activityType === 'hangman') return 'Número de palabras';
    if (activityType === 'linux-quest') return 'Número de niveles';
    if (activityType === 'texthunt') return 'Número de preguntas totales';
    return 'Cantidad';
  };

  const getDefaultCount = () => {
    if (activityType === 'quiz') return 5;
    if (activityType === 'hangman') return 10;
    if (activityType === 'linux-quest') return 3;
    if (activityType === 'texthunt') return 10;
    return 5;
  };

  const getActivityTitle = () => {
    if (activityType === 'quiz') return '📝 Generar Quiz con IA';
    if (activityType === 'hangman') return '🎯 Generar Ahorcado con IA';
    if (activityType === 'linux-quest') return '💻 Generar Terminal Quest con IA';
    if (activityType === 'texthunt') return '📚 Generar TextHunt con IA';
    return 'Generar Actividad con IA';
  };

  const handleGenerate = async () => {
    console.log('🚀 AI Generation started', { activityType, textContentLength: textContent?.length, topicLength: topic?.length });
    
    // Validation
    if (activityType === 'texthunt') {
      if (!textContent.trim()) {
        setError('Por favor introduce el texto base para la comprensión lectora');
        return;
      }
    } else {
      if (!topic.trim()) {
        setError('Por favor introduce un tema');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      let endpoint = '';
      let body = {
        teacher_id: user.id,
        teacher_name: user.name,
        difficulty: difficulty,
        language: 'español'
      };

      if (activityType === 'quiz') {
        endpoint = '/api/ai/generate-quiz';
        body.topic = topic.trim();
        body.num_questions = count;
      } else if (activityType === 'hangman') {
        endpoint = '/api/ai/generate-hangman';
        body.topic = topic.trim();
        body.num_words = count;
      } else if (activityType === 'linux-quest') {
        endpoint = '/api/ai/generate-linux-quest';
        body.topic = topic.trim();
        body.num_levels = count;
      } else if (activityType === 'texthunt') {
        endpoint = '/api/ai/generate-texthunt';
        body.text_content = textContent.trim();
        // Split count between multiple choice and short answer (50/50)
        body.num_multiple_choice = Math.ceil(count / 2);
        body.num_short_answer = Math.floor(count / 2);
      }

      console.log('📡 Sending request:', { endpoint: `${BACKEND_URL}${endpoint}`, bodyKeys: Object.keys(body) });

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log('📥 Response received:', { status: response.status, ok: response.ok });

      const data = await response.json();
      console.log('📦 Response data:', data);

      if (response.ok) {
        console.log('✅ Generation successful');
        onSuccess(data);
        onClose();
      } else {
        console.error('❌ Generation failed:', data);
        setError(data.detail || 'Error al generar la actividad');
      }
    } catch (err) {
      console.error('💥 Exception during generation:', err);
      setError('Error de conexión: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{getActivityTitle()}</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <div style={styles.content}>
          {activityType === 'texthunt' ? (
            <div style={styles.field}>
              <label style={styles.label}>📖 Texto base para comprensión lectora *</label>
              <textarea
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Ingresa aquí el texto que los estudiantes leerán...&#10;&#10;Puede ser un artículo, fragmento literario, noticia, etc."
                style={{...styles.input, minHeight: '200px', fontFamily: 'monospace', fontSize: '14px'}}
                rows="10"
              />
              <p style={{fontSize: '12px', color: '#666', marginTop: '8px'}}>
                💡 La IA generará preguntas de comprensión basadas en este texto
              </p>
            </div>
          ) : (
            <div style={styles.field}>
              <label style={styles.label}>📚 Tema de la actividad *</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Ej: Verbos irregulares en inglés, Sistema solar, Comandos básicos de Linux..."
                style={styles.input}
              />
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>📊 Dificultad</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                style={styles.select}
              >
                <option value="easy">Fácil</option>
                <option value="medium">Media</option>
                <option value="hard">Difícil</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>🔢 {getCountLabel()}</label>
              <input
                type="number"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value))}
                min="1"
                max={activityType === 'quiz' ? '20' : activityType === 'hangman' ? '30' : activityType === 'texthunt' ? '20' : '10'}
                style={styles.input}
              />
              {activityType === 'texthunt' && (
                <p style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                  Se generarán mitad opción múltiple y mitad respuesta corta
                </p>
              )}
            </div>
          </div>

          <div style={styles.infoBox}>
            💡 <strong>Tip:</strong> {activityType === 'texthunt' 
              ? 'La IA analizará el texto y generará preguntas de comprensión lectora.'
              : 'La IA generará una actividad completa basada en el tema.'
            } Podrás revisarla y editarla después de crearla.
          </div>
        </div>

        <div style={styles.footer}>
          <button
            style={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            style={styles.generateButton}
            onClick={handleGenerate}
            disabled={loading || (activityType === 'texthunt' ? !textContent.trim() : !topic.trim())}
          >
            {loading ? '⏳ Generando...' : '🤖 Generar con IA'}
          </button>
        </div>

        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>
              Generando actividad con IA... <br/>
              Esto puede tomar unos segundos
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '15px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    position: 'relative'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '25px',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'all 0.2s'
  },
  content: {
    padding: '25px'
  },
  field: {
    marginBottom: '20px',
    flex: 1
  },
  row: {
    display: 'flex',
    gap: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1976d2',
    marginTop: '10px'
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '15px',
    margin: '0 25px 10px',
    borderRadius: '8px',
    fontSize: '14px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '20px 25px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  generateButton: {
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '15px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '16px',
    color: '#666',
    textAlign: 'center'
  }
};

// Add keyframes for spinner animation
const styleSheet = document.styleSheets[0];
const keyframes =
`@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;
styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

export default AIGeneratorModal;
